import { useDirection } from '@creditornot/cb-provider';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { getLatestKycProcess } from 'apis/legitimizer/kyc/api';
import { isKYCProcessPending } from 'apis/legitimizer/kyc/utils';
import { getOnboardingStatus } from 'apis/merchant-onboarding-service/api';
import { fetchMerchants } from 'apis/restaurant-api/merchant-admin/merchant/api';
import { fetchMe } from 'apis/restaurant-api/merchant-admin/user/api';
import { fetchVenue } from 'apis/restaurant-api/merchant-admin/venue/api';
import { useFeatureFlags } from 'common/hooks';
import useUser from 'common/hooks/useUser';
import { FeatureType } from 'common/types';
import { PWAContextProvider } from 'core/pwa/PWAContext';
import { BestOfWoltCelebration, BestOfWoltIntroduction } from 'modules/best-of-wolt';
import { DeveloperPreviewIndicator } from 'modules/developer-previews';
import useIsEnterpriseUser from 'modules/enterprise/hooks/useIsEnterpriseUser';
import { Header } from 'modules/header';
import { Intercom } from 'modules/intercom';
import { MerchantContextProvider } from 'modules/merchant/MerchantContext';
import useFetchAllMerchantVenues from 'modules/merchant/hooks/useFetchAllMerchantVenues';
import { useIsSupportLayerEnabled } from 'modules/support';
import { VenueContextProvider } from 'modules/venue/VenueContext';

import ExternalPermissionsContext from './ExternalPermissionsContext';
import Loading from './Loading';
import PageLoadError from './PageLoadError';
import Toaster from './Toaster';
import UpdateAvailable from './UpdateAvailable';
import { gaSetCustomUserDimensions } from './googleAnalytics';
import { useAccessRights } from './hooks/useAccessRights';
import { useFetchMerchant } from './hooks/useFetchMerchant';
import RootRoutes from './routes/RootRoutes';
import { CookieConsentsProvider } from '../modules/cookie-consents';
import LoggedOutHeader from '../modules/logged-out-header';

type Params = {
  feature?: FeatureType;
  slug?: string;
};

const Root = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
`;

const titleKeyMap: Record<FeatureType | 'search', string> = {
  'identity-verification': 'navigation.identity-verification',
  venue: 'navigation.venue',
  'venue-group': 'navigation.venue-group',
  merchant: 'navigation.merchant',
  search: 'navigation.search',
  'merchant-sign-up': 'navigation.merchant-sign-up',
  'venue-data-collection': 'navigation.venue-data-collection',
  enterprise: 'navigation.enterprise',
  'photo-shoot': 'navigation.photo-shoot',
  'partner-with-wolt': 'navigation.partner-with-wolt',
  'onboarding-wizard': 'navigation.partner-with-wolt',
  'onboarding-page': 'navigation.partner-with-wolt',
  experience: 'navigation.experience',
};

const App = () => {
  const intl = useIntl();

  const { feature, slug } = useParams<Params>();

  const { user: loggedUser } = useUser();
  const { data: hideIntercomIconFlag } = useFeatureFlags(
    ['hide-intercom-icon'],
    {
      user_id: loggedUser?.id ?? '',
      user_email: loggedUser?.email ?? '',
      user_country: loggedUser?.country ?? '',
      feature: feature ?? '',
    },
    {
      enabled: !!loggedUser,
    },
  );

  const isSupportLayerEnabled = useIsSupportLayerEnabled();
  const isIntercomIconHidden = isSupportLayerEnabled ?? hideIntercomIconFlag?.['hide-intercom-icon'] === 'on';

  useEffect(() => {
    const isUserLoggedIn = !!loggedUser;
    const isUserEmployee = (loggedUser?.roles ?? []).includes('employee');
    gaSetCustomUserDimensions(isUserLoggedIn, isUserEmployee);
  }, [loggedUser]);

  const { pathname } = useLocation();

  const cbDirection = useDirection();

  const isOnFrontPage = pathname === '/';

  const title = intl.formatMessage({ id: titleKeyMap[feature || 'search'] || titleKeyMap.search });

  const isUserFetchEnabled = !!loggedUser;
  const {
    data: user,
    error: userError,
    isLoading: isLoadingUser,
  } = useQuery(
    ['/v1/user/me'],
    () => {
      return fetchMe();
    },
    {
      enabled: isUserFetchEnabled,
    },
  );

  const isMerchantsFetchEnabled = !!loggedUser && !!user;
  const {
    data: merchants,
    error: merchantsError,
    isLoading: isLoadingMerchants,
  } = useQuery(
    ['/v1/merchant-admin/merchants'],
    () => {
      return fetchMerchants();
    },
    {
      enabled: isMerchantsFetchEnabled,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  );

  const isAccessRightsFetchEnabled = !!loggedUser && !!user && feature !== 'identity-verification';

  const {
    accessRights,
    isLoading: isLoadingAccessRights,
    error: accessRightsError,
  } = useAccessRights(isAccessRightsFetchEnabled);

  const isKycProcessFetchEnabled = !!loggedUser;
  const {
    data: kycProcess = null,
    error: kycProcessError,
    isLoading: isLoadingKycProcess,
  } = useQuery(['kyc/processes/latest'], () => getLatestKycProcess(), {
    enabled: isKycProcessFetchEnabled,
  });

  const { data: onboardingStatusData, isLoading: isFetchingOnboardingStatus } = useQuery(
    ['merchant-admin/me/onboarding-status'],
    () => getOnboardingStatus(),
    {
      enabled: isUserFetchEnabled,
    },
  );

  const isLegacyOnboardingFlow = useMemo(
    () => onboardingStatusData?.status === 'LEGACY',
    [onboardingStatusData?.status],
  );

  const [skipIDVerification, setSkipIDVerification] = useState(false);

  const forceIDVerification = !skipIDVerification && isKYCProcessPending(kycProcess) && isLegacyOnboardingFlow;

  const firstMerchantId = merchants?.[0]?.itemid.$oid;
  const merchantId = feature === 'merchant' ? slug : firstMerchantId;

  const isMerchantFetchEnabled = !!user && !!merchantId;
  const { merchant, merchantError, isLoadingMerchant } = useFetchMerchant(merchantId, isMerchantFetchEnabled, {
    // The permissions required to call this endpoint take some time to be granted to the merchant during the SSMO flow
    // Retrying is necessary for a user experience without visual errors
    retry: !isLegacyOnboardingFlow ? 20 : undefined,
    retryDelay: !isLegacyOnboardingFlow ? 5000 : undefined,
  });

  const { isEnterpriseUser, isLoading: isLoadingEnterpriseUser } = useIsEnterpriseUser();
  const isMerchantVenuesFetchEnabled = !!user && !!merchantId && !isLoadingEnterpriseUser && !isEnterpriseUser;

  const {
    venues: merchantVenues,
    error: merchantVenuesError,
    isLoading: isLoadingMerchantVenues,
  } = useFetchAllMerchantVenues(isMerchantsFetchEnabled, merchantId || '');

  const isVenueFetchEnabled = !!user && feature === 'venue' && !!slug;
  const {
    data: venue,
    error: venueError,
    isLoading: isLoadingVenue,
  } = useQuery(
    ['/v1/merchant-admin/venues/', slug],
    () => {
      return slug ? fetchVenue(slug) : undefined;
    },
    {
      enabled: isVenueFetchEnabled,
      refetchOnWindowFocus: false,
    },
  );

  const isLoading =
    (isAccessRightsFetchEnabled && isLoadingAccessRights) ||
    (isKycProcessFetchEnabled && isLoadingKycProcess) ||
    (isMerchantFetchEnabled && isLoadingMerchant) ||
    (isMerchantsFetchEnabled && isLoadingMerchants) ||
    (isMerchantVenuesFetchEnabled && isLoadingMerchantVenues) ||
    (isUserFetchEnabled && isLoadingUser) ||
    (isVenueFetchEnabled && isLoadingVenue) ||
    (isUserFetchEnabled && isFetchingOnboardingStatus);

  const error = !!(
    accessRightsError ||
    kycProcessError ||
    merchantError ||
    merchantsError ||
    merchantVenuesError ||
    userError ||
    venueError
  );

  const showLoggedOutHeader = !user && feature !== 'photo-shoot' && feature !== 'partner-with-wolt';
  const showLoggedInHeader =
    !showLoggedOutHeader &&
    feature !== 'merchant' &&
    feature !== 'enterprise' &&
    feature !== 'venue' &&
    feature !== 'photo-shoot' &&
    feature !== 'experience' &&
    feature !== 'onboarding-page' &&
    feature !== 'partner-with-wolt';

  return (
    <CookieConsentsProvider>
      <MerchantContextProvider value={{ merchant, merchantVenues }}>
        <VenueContextProvider value={venue}>
          <Root lang={intl.locale} dir={cbDirection}>
            <Helmet titleTemplate="%s - Wolt Merchants">
              <title>{title}</title>
              <meta name="description" content={title} />
            </Helmet>
            <PWAContextProvider>
              <ExternalPermissionsContext.Provider value={accessRights || []}>
                {user && (
                  <Intercom
                    appID={process.env.INTERCOM_APP_ID}
                    user={user}
                    iconAlignment={cbDirection === 'ltr' ? 'right' : 'left'}
                    isLauncherHidden={isIntercomIconHidden}
                  />
                )}
                {process.env.APP_ENV === 'production' && <UpdateAvailable />}
                {process.env.APP_ENV !== 'production' && <DeveloperPreviewIndicator />}
                {/* Venue selector is hidden via a flag while App is loading to prevent flashing it while a KYC process is open.
                      Otherwise it flashes right after login when App is loading, before redirecting to '/identity-verification'. */}
                {showLoggedInHeader && (
                  <Header
                    selectedVenueSlug={slug}
                    selectedFeature={feature}
                    hideVenueSelector={isLoading || isOnFrontPage}
                  />
                )}
                {showLoggedOutHeader && <LoggedOutHeader />}
                {error && <PageLoadError />}
                {isLoading && <Loading />}
                {!error && !isLoading && (
                  <RootRoutes forceIDVerification={forceIDVerification} setSkipIDVerification={setSkipIDVerification} />
                )}
                {feature === 'venue' && venue && <BestOfWoltCelebration venue={venue} />}
                {feature === 'venue' && venue && <BestOfWoltIntroduction venue={venue} />}
              </ExternalPermissionsContext.Provider>
            </PWAContextProvider>
          </Root>
          <Toaster />
        </VenueContextProvider>
      </MerchantContextProvider>
    </CookieConsentsProvider>
  );
};

export default App;
